import { lazy, Suspense, useState, useRef, useEffect } from 'react'

// React's built-in lazy loading - component code only loads when needed
const MapRender = lazy(() => import('./mapRender'))

export interface LazyMapProps {
  hiringArea: any
  allStatesCoordinates: any
}

function MapSkeleton() {
  return (
    <div className="flex items-center justify-center w-full h-full bg-slate-50 border border-gray-200 rounded-lg">
      <div className="text-center">
        <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
        <p className="text-slate-600 text-sm">Loading map...</p>
      </div>
    </div>
  )
}

function MapPlaceholder({ onClick }: { onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="w-full h-full flex flex-col items-center justify-center bg-slate-50 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:border-blue-600 hover:bg-blue-50">
      <div className="bg-gray-200 rounded-full w-12 h-12 flex items-center justify-center mb-3">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            fill="#9ca3af"
          />
        </svg>
      </div>
      <p className="text-slate-600 text-sm font-medium mb-1 text-center">
        Hiring Area Map
      </p>
      <p className="text-slate-400 text-xs text-center">
        Click to view the current hiring area map
      </p>
    </div>
  )
}

export default function ReactLazyMap({
  hiringArea,
  allStatesCoordinates
}: LazyMapProps) {
  const [shouldRender, setShouldRender] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Client-side intersection observer for automatic loading
  useEffect(() => {
    if (typeof window === 'undefined') return // Skip on server (crawler-friendly)

    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries

        // Optional: Auto-load when user scrolls to it
        if (entry.isIntersecting && !shouldRender) {
          // Uncomment next line if you want automatic loading when visible
          // setShouldRender(true)

          observer.disconnect()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    )

    observer.observe(container)
    return () => observer.disconnect()
  }, [shouldRender])

  // Handle manual loading
  const handleLoadMap = () => {
    setShouldRender(true)
  }

  // Server-side: render empty placeholder (crawler sees nothing)
  if (typeof window === 'undefined') {
    return (
      <div
        style={{
          maxWidth: '1200px',
          width: '100%',
          height: '50vw',
          maxHeight: '600px',
          margin: '0'
        }}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        maxWidth: '1200px',
        width: '100%',
        height: '50vw',
        maxHeight: '600px',
        margin: '0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
      {shouldRender ? (
        <Suspense fallback={<MapSkeleton />}>
          <MapRender
            hiringArea={hiringArea}
            allStatesCoordinates={allStatesCoordinates}
          />
        </Suspense>
      ) : (
        <MapPlaceholder onClick={handleLoadMap} />
      )}
    </div>
  )
}
